import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useInfiniteQuery, type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { z } from "zod";

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const searchFacilitiesParamsSchema = z.object({
  searchInput: z.string().nullish(),
  limit: z.number().optional(),
});

export const facilitySchema = z.object({
  _id: z.string(),
  userId: z.string(),
  name: z.string(),
  highlights: z.array(z.string()),
  pricingMsa: z.string(),
  type: z.string(),
  status: z.string(),
});

const searchFacilitiesResponseSchema = z.object({
  list: z.array(facilitySchema),
});

type UseSearchFacilitiesParams = z.infer<typeof searchFacilitiesParamsSchema>;
type SearchFacilitiesResponse = z.infer<typeof searchFacilitiesResponseSchema>;
export type Facility = z.infer<typeof facilitySchema>;

const DEFAULT_PAGE_SIZE = 15;

export function useSearchFacilities(
  params: UseSearchFacilitiesParams,
  options?: UseInfiniteQueryOptions<SearchFacilitiesResponse>
) {
  const { limit = DEFAULT_PAGE_SIZE, searchInput } = params;
  const url = `${environmentConfig.REACT_APP_BASE_API_URL}/user/facilitySearch`;

  return useInfiniteQuery({
    queryKey: [url, searchInput?.trim().toLowerCase(), limit],
    queryFn: async (context) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { pageParam: skip = 0 } = context;
      const response = await get({
        url,
        queryParams: {
          skip,
          limit,
          searchInput: searchInput ?? "",
        },
        responseSchema: searchFacilitiesResponseSchema,
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.list.length < limit) {
        return undefined;
      }

      return allPages.flatMap((page) => page.list).length; // return the new "skip" value for the next page
    },
    enabled: isDefined(searchInput) && !isEmpty(searchInput) && (options?.enabled ?? true),
    staleTime: 60 * 1000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.SEARCH_FACILITIES_FAILURE,
      userErrorMessage: "Failed to search workplaces. Please try again.",
    },
    ...options,
  });
}
